import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Edit2, Trash2, Calendar, Clock, CalendarDays, CalendarRange, CalendarCheck, History } from 'lucide-react';
import { Task, db } from '../../lib/db';
import TaskForm from './TaskForm';
import { calculateNextDueDate, formatDueDate, parseDateOnlyToLocal } from '../../lib/dateUtils';

interface RegularTaskTemplatesProps {
  templates: Task[];
  lastCompletions: Map<string, Date | null>;
  onTaskCreate: (task: Partial<Task>) => Promise<void>;
  onTaskUpdate: (id: string, updates: Partial<Task>) => Promise<void>;
  onTaskDelete: (id: string) => Promise<void>;
  onTaskClick?: (task: Task) => void;
  dbUserId: string;
}

function RegularTaskTemplates({ templates, lastCompletions, onTaskCreate, onTaskUpdate, onTaskDelete, onTaskClick, dbUserId }: RegularTaskTemplatesProps) {
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [avgMinutesByTask, setAvgMinutesByTask] = useState<Map<string, number>>(new Map());
  const [nextBySignature, setNextBySignature] = useState<Map<string, Date>>(new Map());

  const getFrequencyIcon = (frequency: string) => {
    switch (frequency) {
      case 'Daily':
        return <Clock className="w-4 h-4" />;
      case 'Weekdays':
        return <CalendarDays className="w-4 h-4" />;
      case 'Weekly':
        return <CalendarDays className="w-4 h-4" />;
      case 'Monthly':
        return <Calendar className="w-4 h-4" />;
      case 'Quarterly':
        return <CalendarRange className="w-4 h-4" />;
      case 'Annually':
        return <CalendarCheck className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getFrequencyDescription = (task: Task) => {
    const frequency = task.frequency || 'Daily';
    
    if (frequency === 'Weekly' && task.recurrence_day !== null && task.recurrence_day !== undefined) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return `${frequency} (${days[task.recurrence_day]})`;
    }
    
    return frequency;
  };

  // Load average minutes per completion for the template list
  useEffect(() => {
    const loadAverages = async () => {
      if (!templates.length) {
        setAvgMinutesByTask(new Map());
        return;
      }
      try {
        const identifiers = templates.map(t => ({ title: t.title, description: t.description || '' }));
        const result = await db.taskCompletionStatistics.getBatchEstimates(identifiers as any, dbUserId);
        setAvgMinutesByTask(result);
      } catch (e) {
        // Non-fatal: just hide averages if lookup fails
        console.error('Failed to load average times for templates', e);
        setAvgMinutesByTask(new Map());
      }
    };
    void loadAverages();
  }, [templates, dbUserId]);

  // Build a map of the next due date per regular task signature by looking at active (incomplete) instances.
  useEffect(() => {
    const loadNextMap = async () => {
      if (!dbUserId) return;
      try {
        const active = await db.tasks.findActiveRegularTasks(dbUserId);
        const map = new Map<string, Date>();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const toLocalMidnight = (d: Date) => {
          const dt = new Date(d);
          dt.setHours(0, 0, 0, 0);
          return dt;
        };

        for (const t of active) {
          const key = `${(t.title || '').trim().toLowerCase()}|${(t.description || '').trim().toLowerCase()}|${(t.frequency || 'Daily').trim()}`;
          let dt: Date | null = null;
          if (t.next_due_date) {
            dt = parseDateOnlyToLocal(t.next_due_date as any);
          } else if (t.start_date) {
            dt = toLocalMidnight(new Date(t.start_date));
          }
          if (!dt) continue;

          // If somehow the date is in the past, advance once based on frequency to avoid showing "Today" incorrectly post-completion.
          if (dt <= today) {
            dt = calculateNextDueDate(dt, (t.frequency || 'Daily') as any, t.recurrence_day ?? undefined);
          }

          const existing = map.get(key);
          if (!existing || (dt.getTime() < existing.getTime())) {
            map.set(key, dt);
          }
        }
        setNextBySignature(map);
      } catch (e) {
        console.error('Failed to build next-due map for templates', e);
        setNextBySignature(new Map());
      }
    };
    void loadNextMap();
  }, [dbUserId, templates]);

  // Helper to decide the displayed next date for a template row
  const getNextForTemplate = (task: Task): Date | null => {
    const key = `${(task.title || '').trim().toLowerCase()}|${(task.description || '').trim().toLowerCase()}|${(task.frequency || 'Daily').trim()}`;
    const fromActive = nextBySignature.get(key);
    if (fromActive) return fromActive;

    // Fall back to computing from last completion if available
    const lastForTitle = lastCompletions.get(task.title || '');
    if (lastForTitle) {
      return calculateNextDueDate(new Date(lastForTitle), (task.frequency || 'Daily') as any, task.recurrence_day ?? undefined);
    }

    // As a final fallback, try the stored next_due_date on template, respecting local midnight parsing
    if (task.next_due_date) {
      return parseDateOnlyToLocal(task.next_due_date as any);
    }

    return null;
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this regular task? This will delete ALL instances of this task (including any incomplete ones).')) {
      await onTaskDelete(id);
    }
  };

  const formatLastCompletion = (date: Date | null | undefined) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    
    return formatDueDate(date);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Existing Regular Tasks</h3>
        <p className="text-sm text-gray-600 mt-1">
          These tasks automatically regenerate based on their frequency
        </p>
      </div>
      
      <div className="divide-y divide-gray-200">
        {templates.length === 0 && !showNewTaskForm && (
          <div className="p-6 sm:p-8 text-center text-gray-500">
            No regular tasks set up yet. Create one to start building habits!
          </div>
        )}
        
        {templates.map((task) => (
          <div key={task.id} className="p-4 sm:p-6">
            {editingTaskId === task.id ? (
              <TaskForm
                task={task}
                onSubmit={async (updates) => {
                  await onTaskUpdate(task.id, updates);
                  setEditingTaskId(null);
                }}
                onCancel={() => setEditingTaskId(null)}
                onDelete={onTaskDelete}
                availableTasks={[]}
                showFrequency={true}
              />
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex-shrink-0">
                      {getFrequencyIcon(task.frequency || 'Daily')}
                    </div>
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => onTaskClick && onTaskClick(task)}
                    >
                      <div className="flex items-start justify-between sm:block">
                        <h4 className="font-medium text-gray-900 hover:text-gray-700">{task.title}</h4>
                        <span className={`sm:hidden ml-2 px-2 py-1 text-xs rounded-full ${
                          task.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                          task.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                          task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.priority || 'Low'}
                        </span>
                      </div>
                      {task.description && (
                        <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                      )}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Last: {formatLastCompletion(lastCompletions.get(task.title))}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          {getFrequencyIcon(task.frequency || 'Daily')}
                          {getFrequencyDescription(task)}
                        </span>
                        {(() => {
                          const next = getNextForTemplate(task);
                          if (!next) return null;
                          return (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {`Next: ${formatDueDate(next)}`}
                          </span>
                          );
                        })()}
                        <span className="text-xs text-blue-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {(() => {
                            const key = `${task.title}|${task.description || ''}`;
                            const minutes = avgMinutesByTask.get(key);
                            if (!minutes || minutes <= 0) return 'Avg: --';
                            const m = Math.round(minutes);
                            if (m < 60) return `Avg: ${m}m`;
                            const h = Math.floor(m / 60);
                            const rem = m % 60;
                            return `Avg: ${h}h${rem ? ` ${rem}m` : ''}`;
                          })()}
                        </span>
                        <span className={`hidden sm:inline-flex px-2 py-1 text-xs rounded-full ${
                          task.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                          task.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                          task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.priority || 'Low'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-1 sm:gap-2 justify-end sm:justify-start">
                  <button
                    onClick={() => onTaskClick && onTaskClick(task)}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    title="View history"
                  >
                    <History className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEditingTaskId(task.id)}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Edit regular task"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(task.id)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete regular task"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="p-4 sm:p-6">
        {!showNewTaskForm ? (
          <button
            onClick={() => setShowNewTaskForm(true)}
            className="w-full py-3 px-4 border border-gray-300 border-dashed rounded-lg text-gray-500 hover:text-gray-700 hover:border-gray-400 flex items-center justify-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Regular Task
          </button>
        ) : (
          <TaskForm
            onSubmit={async (task) => {
              await onTaskCreate({
                ...task,
                is_regular: true,
                frequency: task.frequency || 'Daily',
                recurrence_day: task.recurrence_day
              });
              setShowNewTaskForm(false);
            }}
            onCancel={() => setShowNewTaskForm(false)}
            availableTasks={[]}
            showFrequency={true}
          />
        )}
      </div>
    </div>
  );
}

export default RegularTaskTemplates;
